/**
 * Opening a signing ceremony from inside the console.
 *
 * One function, in its own module, because two screens need it and neither should own it. The
 * inbox has always had it; the dashboard's signature queue now does too, and a dashboard that
 * counted the documents waiting on somebody and then made them navigate to press the button would
 * be a scoreboard again — which is the one thing `ux.md` §6.1 says the landing page must not be.
 *
 * The duplication this avoids is not cosmetic. The path check below is a refusal to turn an
 * unexpected answer into an open redirect, and a second copy of it is a second copy that can be
 * relaxed by somebody who did not read this comment.
 */
import { ApiError, api } from './client'

/**
 * Open a ceremony for a recipient row that is the caller's own.
 *
 * The API answers with a PATH, never a full URL, and this navigates with a full page load rather
 * than the router: the ceremony is a separate bundle entry (`sign.html`) with no shell, no auth
 * client and no router, which is what keeps a signer's browser from downloading the console.
 *
 * `from` names the screen the press came from. It carries no authority — the ceremony treats it as
 * a UI flag and nothing else — and it exists because the ceremony is a separate bundle with no
 * shell and no router, so it cannot otherwise know it was reached from here rather than from an
 * email.
 */
export async function openCeremony(
  envelopeSignerId: string,
  from: 'inbox' | 'dashboard' = 'inbox',
): Promise<void> {
  const body = await api<{ readonly ceremony_path: string }>(`/v1/inbox/${envelopeSignerId}/open`, {
    method: 'POST',
    body: {},
  })
  const path = body.ceremony_path
  // Shape-checked before it becomes a navigation. The value comes from our own API, so this is not
  // a trust boundary so much as a refusal to turn an unexpected answer into an open redirect.
  if (!/^\/s\/[A-Za-z0-9_-]{43}$/.test(path)) {
    throw new ApiError(0, 'unexpected', 'the server did not return a usable signing link')
  }
  window.location.assign(`${path}?from=${from}`)
}
